/**
 * session-claim-source.ts — the real session-claim source App.tsx's
 * composition root consumes (TG-3, ADR-20261008-1enh, ADR-20261015-c9zb).
 *
 * REPLACES `session-claim-stand-in.ts` (D-22's interim unconditional-owner
 * claim, deleted by this feature per ADR-20261008-1enh's own forward
 * obligation: "session-claim-stand-in.ts and its App.tsx call site MUST
 * be deleted once the real magic-link/session-claim feature ships"). The
 * magic-link feature has shipped (`m1-magic-link-authentication-session`)
 * and hardened its session surface (TG-1 of this feature); this file is
 * the one place `capability/`'s own composition-root entry point reads
 * the REAL, verified session claim from.
 *
 * Lives inside `capability/` specifically so the literal `principalRole`
 * field name the claim's underlying shape carries is never spelled by
 * `App.tsx` or `portal-auth/` (ADR-20260826-79rv's containment rule,
 * proved by `__tests__/containment.test.ts`'s repository-wide textual
 * search, AC-1): `portal-auth/token.ts`'s `getSessionClaim` deliberately
 * returns the JWT's own `claim` property as `unknown`, naming no field
 * inside it, and `getCurrentRawClaim` below is the one place that cast
 * happens, exactly where `session-claim-stand-in.ts`'s own
 * `buildStandInOwnerClaim` used to construct the (fabricated) value with
 * the same type.
 *
 * Deliberately not re-exported from `./index` (ADR-20260826-79rv: "only
 * the Capability object and a narrow renewalId accessor leave the
 * boundary") -- a composition root reaches this the same deep-import way
 * `session-claim-stand-in.ts` was reached, and the same way
 * `use-capability.ts` already re-exports `CapabilityProvider` without
 * widening the barrel.
 */
import { getSessionClaim } from "../portal-auth/token";
import type { decodeClaim } from "./claim";

/**
 * Reads the CURRENT session's raw claim, decoded from whichever JWT
 * `portal-auth/token.ts` holds right now (or `null` with no session) --
 * this is a snapshot read, not a subscription; pair it with
 * {@link subscribeToSessionClaimChange} (e.g. via `useSyncExternalStore`)
 * to re-read it whenever sign-in, redeem, refresh or sign-out changes
 * which token (if any) is current. Every validation `decodeClaim` applies
 * (closed field set, a recognised role, well-formed delegated section
 * ids) still runs on whatever this returns -- the cast below only
 * satisfies the type checker, it does not assert the value is trustworthy
 * shaped.
 */
export function getCurrentRawClaim(): Parameters<typeof decodeClaim>[0] {
  return getSessionClaim() as Parameters<typeof decodeClaim>[0];
}

/**
 * Re-exported under this file's own name (rather than consumers reaching
 * `portal-auth/token.ts` directly for this one function) so App.tsx's
 * composition root has a single import for both the snapshot and the
 * subscription half of this claim source, mirroring the shape
 * `useSyncExternalStore` itself expects.
 */
export { subscribeTokenChange as subscribeToSessionClaimChange } from "../portal-auth/token";
